import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import SeaVista from './SeaVista';
import CameraBasisProbe from './CameraBasisProbe';
import { useWorldStore } from '../state/worldStore';
import { api } from '../services/api';
import { proxied } from '../services/assets';
import { audioEngine } from '../services/audioEngine';
import { useWorldTime } from '../worldTime';
import { SLICE_DISTRICT, IS_MOBILE } from '../config';
import { ROOMS } from '../rooms';
import { ensureHouseFill, onHouseFill, houseFillItem } from '../services/houseFill';
import { registerVideo, updateVideo } from '../services/videoPool';
import { loadTexture } from '../services/textureLoad';
import {
  loadEpisodeFeed, currentIndexOf, feedOf,
  setScreenLevel, clearScreenLevel, isPodcastMuted, setPodcastMuted, onPodcastMute,
  acquireEpisodeVideo, releaseEpisodeVideo,
} from '../services/newsPodcast';
import {
  setBaseVolume, screenVolume, tickScreens, registerScreen, unregisterScreen,
} from '../services/screenVolume';
import PortalSurface, { isPortal } from './PortalSurface';

// PlacementLayer — the in-scene half of the promotions system (spec §2/§4/§7):
//   • renders every saved PLACEMENT (a permanent display surface) as a quad with
//     a frame prop and night-driven emissive glow;
//   • in the admin placement tool (AdminPanel → "Ad placements") it also renders
//     the GHOST quad that follows your crosshair, snapped to whatever wall you
//     look at, plus wireframes + labels over existing surfaces.
// Bookings/creatives land later (§5/§6) — for now a surface shows its
// default_content_ref still, else a labelled house placeholder, so inventory is
// never blank and you can see what you placed.

export const ASPECT_RATIO = { '1:1': 1, '16:9': 16 / 9, '4:3': 4 / 3, '3:4': 3 / 4, '9:16': 9 / 16 };

// Flat backing plate per style. `nameplate` is null because it builds its own
// 3D housing (box + recessed plate) further down.
const FRAME_COLOR = { bare: null, framed: '#0d0a08', neon: '#ff5e78', led_panel: '#16161c', nameplate: null };

// One saved surface. `ghost` renders it translucent (the tool's preview).
const noRaycast = () => null;

// Which SHOW a content type is a television for. Only these two can be set to
// `playback: 'play'` — a featured track has no episode to run, so a "playing"
// track surface would be a rotation with extra steps.
const PODCAST_KIND = { interview_podcast: 'interview', news_broadcast: 'news' };

// THE TIGHT BOUND, and the slide into it (world units).
//
// Khari, 2026-08-04: "at distance would be set to 50% volume.. its playing in
// the room.. within the tight bounds it plays at 100%, possibly sliding up and
// down as I approach that bound."
//
// So FULL is a PLATEAU, not a peak. The first version eased toward 1.0 as
// distance → 0, which meant you only ever got full volume by standing inside
// the wall: at a realistic 0.3u it reached 87% and never 100%. A screen you can
// walk up to has to actually arrive.
//
//   ≤ FULL_U        100%. You are at it.
//   FULL_U…SLIDE_U  slides between full and the room level — the "up and down
//                   as I approach" part, and what makes it read as a room
//                   rather than as a switch.
//   ≥ SLIDE_U       the room level, and the trigger's own falloff takes over.
//
// 2.4 is the roster's `attentionRadius` — the world's existing answer to "you
// have walked up to this thing" — so arriving at a television feels like
// arriving at a person. 5.0 puts the slide across a stride or two beyond it.
const FULL_U = 2.4;
const SLIDE_U = 5.0;

// Eye height, mirroring PlanetScene's EYE (and its ?eye= override) so the range
// rings land on the plane the player actually walks on rather than a guess.
const EYE = (() => {
  const q = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('eye'));
  return Number.isFinite(q) && q > 0 ? q : 0.75;
})();

// Scratch objects for the per-frame ring maths — allocated once, because this
// runs every frame for every surface and three's Vector3/Quaternion churn is a
// real cost in a city with a lot of them.
const TMP_V = new THREE.Vector3();
const TMP_Q = new THREE.Quaternion();
const FLAT_Q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

// One clickable album cover in the opened billboard's grid. Click = play.
function TrackTile({ track, size, position }) {
  const [tex, setTex] = useState(null);
  const [hover, setHover] = useState(false);
  const ref = useRef();
  useEffect(() => {
    let dead = false;
    loadTexture(track.artwork_url).then((t) => { if (!dead && t) setTex(t); });
    return () => { dead = true; };
  }, [track.artwork_url]);
  useFrame(() => {
    if (!ref.current) return;
    const target = hover ? 1.07 : 1;
    ref.current.scale.x += (target - ref.current.scale.x) * 0.2;
    ref.current.scale.y = ref.current.scale.x;
  });
  return (
    <group position={position}>
      <mesh
        ref={ref}
        onClick={(e) => { e.stopPropagation(); audioEngine.playTrack(track); }}
        onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[size, size]} />
        {/* KEYED so the texture's arrival builds a FRESH material. Swapping props
            on the existing one leaves it compiled without USE_MAP (three only
            recompiles on needsUpdate) and drops the color prop back to its white
            default — the "white box until you reopen it" bug. */}
        <meshBasicMaterial
          key={tex ? tex.uuid : 'blank'}
          map={tex || null}
          color={tex ? '#ffffff' : '#1a1520'}
          toneMapped={false}
        />
      </mesh>
      {/* hover ring so it's obvious these are buttons */}
      {hover && (
        <lineSegments position={[0, 0, 0.004]}>
          <edgesGeometry args={[new THREE.PlaneGeometry(size * 1.08, size * 1.08)]} />
          <lineBasicMaterial color="#ffb25e" />
        </lineSegments>
      )}
    </group>
  );
}

// THE CAPTION OF A POST, over its picture (a "Latest posts" board, houseFill
// postsPool) — the click that opens an artist's board on their tracks shows a
// post's words here instead: who posted it, and what they wrote. A click on it
// closes it again; the board holds that post while it is open (Surface).
const CAPTION_MAX = 420;
function PostCaption({ item, w, h, onClose }) {
  const words = String(item.caption || '').replace(/\s+\n/g, '\n').trim();
  const text = words.length > CAPTION_MAX ? `${words.slice(0, CAPTION_MAX - 1).trimEnd()}…` : (words || 'No caption on this one.');
  const s = Math.min(w, h);
  // Shorter words, bigger letters — a long caption still fits the square.
  const fs = s * (text.length <= 120 ? 0.062 : text.length <= 260 ? 0.048 : 0.038);
  const pad = s * 0.07;
  return (
    <group position={[0, 0, 0.03]}>
      <mesh
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[w, h]} />
        <meshBasicMaterial color="#08060a" transparent opacity={0.88} toneMapped={false} />
      </mesh>
      <Text position={[-w / 2 + pad, h / 2 - pad, 0.01]} fontSize={s * 0.07} color="#ffd27d" anchorX="left" anchorY="top" maxWidth={w - pad * 2} toneMapped={false}>
        {item.name || 'An artist'}
      </Text>
      {item.sub && (
        <Text position={[-w / 2 + pad, h / 2 - pad - s * 0.09, 0.01]} fontSize={s * 0.042} color="#ff9fd0" anchorX="left" anchorY="top" maxWidth={w - pad * 2} toneMapped={false}>
          {item.sub}
        </Text>
      )}
      <Text position={[-w / 2 + pad, h / 2 - pad - s * 0.19, 0.01]} fontSize={fs} lineHeight={1.3} color="#f4ece4" anchorX="left" anchorY="top" maxWidth={w - pad * 2} toneMapped={false}>
        {text}
      </Text>
      <Text position={[0, -h / 2 + pad * 0.6, 0.01]} fontSize={s * 0.034} color="#9a8e84" anchorX="center" anchorY="middle" toneMapped={false}>
        tap to close
      </Text>
    </group>
  );
}

// The opened billboard: the artist's tracks as a GRID OF ARTWORK (covers read far
// better than a list of titles at billboard distance). Order comes straight from
// /api/tracks/avatar/:id — pinned first, then newest, exactly like the portfolio.
function BillboardTracks({ avatarId, name, w, h, onClose }) {
  const [tracks, setTracks] = useState(null);
  useEffect(() => {
    let dead = false;
    api.avatarTracks(avatarId)
      .then((d) => { if (!dead) setTracks((d?.tracks || []).slice(0, 9)); })
      .catch(() => { if (!dead) setTracks([]); });
    return () => { dead = true; };
  }, [avatarId]);

  const pad = Math.min(w, h) * 0.03;
  // No title here — the nameplate under the artwork already names the artist.
  // Just a slim band so the × has room; everything else goes to the covers.
  const headerH = h * 0.04;
  const cols = w >= h ? 3 : 2;
  const list = (tracks || []).slice(0, cols * 3);
  const rows = Math.max(1, Math.ceil(list.length / cols));
  const gap = Math.min(w, h) * 0.018;
  const gw = w - pad * 2;
  const gh = h - headerH - pad * 2;
  const cell = Math.max(0.05, Math.min((gw - gap * (cols - 1)) / cols, (gh - gap * (rows - 1)) / rows));
  const totalW = cols * cell + (cols - 1) * gap;
  const totalH = rows * cell + (rows - 1) * gap;
  const areaCenterY = ((h / 2 - headerH) + (-h / 2 + pad)) / 2;

  return (
    <group position={[0, 0, 0.03]}>
      {/* scrim so the covers read against whatever art is behind */}
      <mesh>
        <planeGeometry args={[w, h]} />
        <meshBasicMaterial color="#08060a" transparent opacity={0.93} toneMapped={false} />
      </mesh>
      {/* close */}
      <mesh
        position={[w / 2 - pad * 0.7, h / 2 - pad * 0.7, 0.01]}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'auto'; }}
      >
        <circleGeometry args={[Math.min(w, h) * 0.045, 24]} />
        <meshBasicMaterial color="#2a1f16" toneMapped={false} />
      </mesh>
      <Text position={[w / 2 - pad * 0.7, h / 2 - pad * 0.7, 0.02]} fontSize={Math.min(w, h) * 0.05} color="#ffd27d" anchorX="center" anchorY="middle" toneMapped={false}>×</Text>

      {tracks === null && (
        <Text position={[0, areaCenterY, 0.01]} fontSize={Math.min(w, h) * 0.045} color="#ffb25e" anchorX="center" anchorY="middle">loading…</Text>
      )}
      {tracks !== null && list.length === 0 && (
        <Text position={[0, areaCenterY, 0.01]} fontSize={Math.min(w, h) * 0.045} color="#ffb25e" anchorX="center" anchorY="middle">no released tracks yet</Text>
      )}
      {list.map((t, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        return (
          <TrackTile
            key={t.track_id}
            track={t}
            size={cell}
            position={[
              -totalW / 2 + cell / 2 + c * (cell + gap),
              areaCenterY + totalH / 2 - cell / 2 - r * (cell + gap),
              0.01,
            ]}
          />
        );
      })}
    </group>
  );
}

// DESKTOP BREATHES; PHONES SPEND THEIR VIDEO BUDGET ELSEWHERE.
//
// On desktop every surface with a breathing image plays it automatically. That
// costs no more than it ever did, because the video pool still decides WHICH
// ones actually decode (nearest + on-screen win, MAX_VIDEOS of them) — "all of
// them" is a preference, not a budget.
//
// Phones are a BUDGET decision, not a taste one. Animated is always preferred
// where it can be afforded; a phone can only decode ~3 videos before Safari
// starts stalling or OOM-crashing, and those slots are worth more to the room's
// own screens (the studio cameo, the cinema) than to wall art. Placements
// therefore stand down on mobile rather than competing for them.
//
// Flip this one constant to give phones breathing placements back — the pool
// caps the cost either way, so it is safe to try.
const PLACEMENT_VIDEO_ON_MOBILE = false;

export function Surface({ p, night = 0, ghost = false, selected = false, onSelect = null, onActivate = null, active = false }) {
  const [tex, setTex] = useState(null);
  // Content resolution (spec §6): an explicit default_content_ref wins; otherwise
  // HOUSE FILL picks live HypeTunes content matching this surface's type, so a
  // placement with no booking is never blank. Bookings (promotions) slot in above
  // this later. `fillTick` re-runs the pick once the shared pools land.
  const [fillTick, setFillTick] = useState(0);
  useEffect(() => {
    if (ghost || p.default_content_ref) return undefined;
    ensureHouseFill().then(() => setFillTick((n) => n + 1));
    return onHouseFill(() => setFillTick((n) => n + 1));
  }, [ghost, p.default_content_ref]);
  // NOTE: content_policy + aspect MUST be deps — house fill picks its pool from
  // them, so editing a surface's content type has to re-resolve the media (that
  // omission is why an edited surface kept showing its old/blank content).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // ── A SCREEN THAT PLAYS, rather than one that changes picture ──────────────
  //
  // Khari, 2026-08-04: "the interview podcasts are just rotating.. they should
  // be playing episodes... Both of those should just be 'on'."
  //
  // `playback: 'play'` (migration 285) runs the episode: visual_timeline slides
  // advanced off the shared wall clock, so every screen and every client shows
  // the same moment without any state being stored. House-fill rotation stays
  // exactly as it was for every other surface, and remains selectable here — a
  // wall of rotating episode covers is a legitimate poster, it just is not a
  // television.
  const podKind = PODCAST_KIND[(p.content_policy || [])[0]] || null;
  const episodeMode = !ghost && p.playback === 'play' && !!podKind;
  const [cueTick, setCueTick] = useState(0);
  const cueRef = useRef(-1);

  const [feedVideo, setFeedVideo] = useState(null);
  useEffect(() => {
    if (!episodeMode) return undefined;
    let dead = false;
    loadEpisodeFeed(podKind).then((f) => { if (!dead) { setCueTick((n) => n + 1); setFeedVideo(f?.videoUrl || null); } }).catch(() => {});
    return () => { dead = true; };
  }, [episodeMode, podKind]);

  // THE FILMED SHOW: when the episode has a video, this screen shows the one
  // shared film (services/newsPodcast) instead of the slides.
  const [filmTex, setFilmTex] = useState(null);
  useEffect(() => {
    if (!episodeMode || !feedVideo) return undefined;
    setFilmTex(acquireEpisodeVideo(podKind));
    return () => { releaseEpisodeVideo(podKind); setFilmTex(null); };
  }, [episodeMode, podKind, feedVideo]);

  // One poll per screen, cheap: the cue index is pure arithmetic on the clock,
  // and we only re-render when it actually changes slide.
  useFrame(() => {
    if (!episodeMode) return;
    const i = currentIndexOf(podKind);
    if (i !== cueRef.current) { cueRef.current = i; setCueTick((n) => n + 1); }
  });

  // A "Latest posts" board (houseFill postsPool): a click shows the post's
  // caption, and the board HOLDS that post while it is open — reading a caption
  // that turns over to someone else's mid-sentence would defeat the point.
  const postsBoard = (p.content_policy || [])[0] === 'latest_posts';
  const heldPost = useRef(null);
  const media = useMemo(() => {
    if (ghost) return null;
    if (postsBoard && active && heldPost.current) return heldPost.current;
    if (episodeMode) {
      const f = feedOf(podKind);
      const cue = f?.cues?.[cueRef.current >= 0 ? cueRef.current : 0];
      // Falls through to house fill until the feed lands, so a playing screen is
      // never a blank rectangle while it loads.
      if (cue?.url) return { still: cue.url, animated: null, name: f.show, sub: f.title, playing: true };
    }
    if (p.default_content_ref) {
      // Pinned by an explicit ref — rotation deliberately does not apply.
      // Logged because a pinned surface and a broken rotation look identical.
      console.log('[houseFill] #' + p.placement_id + ' is PINNED to ' + p.default_content_ref + ' — not rotating');
      return { still: p.default_content_ref, animated: null };
    }
    const item = houseFillItem(p);
    if (postsBoard) heldPost.current = item;
    return item;
  }, [p.default_content_ref, p.placement_id, p.aspect, (p.content_policy || []).join(','), ghost, fillTick,
    episodeMode, podKind, cueTick, postsBoard, active]);
  const url = media?.still || null;

  // ── ANIMATED variant, governed by the global video pool ───────────────────
  // "Prefer animation when available" — but only while this surface holds one of
  // the few decode slots (nearest + on-screen win). Otherwise the still shows.
  const [playing, setPlaying] = useState(false);
  const [vidTex, setVidTex] = useState(null);
  const groupRef = useRef();
  const animated = (IS_MOBILE && !PLACEMENT_VIDEO_ON_MOBILE) ? null : (media?.animated || null);

  useEffect(() => {
    if (!animated || ghost) return undefined;
    return registerVideo(`placement-${p.placement_id}`, setPlaying);
  }, [animated, ghost, p.placement_id]);

  // Report distance + rough on-screen state so the pool can rank us, and — for a
  // screen that can make noise — turn that same distance into a LEVEL.
  const lvlRef = useRef(-1);
  const ringRef = useRef();
  useFrame(({ camera }, delta) => {
    const needsPos = (animated || episodeMode) && !ghost && groupRef.current;
    if (!needsPos) return;
    const wp = groupRef.current.getWorldPosition(new THREE.Vector3());
    const dist = camera.position.distanceTo(wp);
    if (animated) {
      const toCam = wp.clone().sub(camera.position).normalize();
      const fwd = camera.getWorldDirection(new THREE.Vector3());
      updateVideo(`placement-${p.placement_id}`, dist, toCam.dot(fwd) > 0.35 && dist < 90);
    }
    if (!episodeMode) return;

    // Step every knob. Safe to call from every screen — tickScreens throttles
    // itself to one step per frame, so N surfaces cost one short Map walk rather
    // than N, and no component has to be elected to own the tick.
    tickScreens();

    // ── HOW LOUD, FROM HERE ───────────────────────────────────────────────
    //
    // Khari, 2026-08-04: "the tv plays at 50% in the salon, but if I were
    // standing directly next to it, I would hear it louder... I am trying to
    // figure out how to allow someone to hear full volume without adding a
    // volume slider. In real life, it would either be manually turned up, or
    // you would walk closer."
    //
    // So WALKING CLOSER IS THE VOLUME CONTROL, and no player-facing slider has
    // to exist. `volume` stops meaning "the loudest this can ever be" and starts
    // meaning "the level it sits at in the room" — the first version multiplied
    // by proximity, which capped a 50% television at 50% from any distance and
    // left no way to hear it over anything else.
    //
    // Two segments, and they compose rather than compete:
    //   ROOM LEVEL   the trigger decides whether you hear it at all, and at the
    //                set volume — walking into earshot, or always, or hover.
    //   LEAN-IN      inside LEAN_M, the level lifts from there toward FULL,
    //                whatever the trigger. This is the half that lets somebody
    //                hear it over the station, the pulse, and the barks.
    const knob = screenVolume(p.placement_id, baseVol);
    let level = 0;
    if (trigger === 'always') level = knob;
    else if (trigger === 'hover') level = hoverRef.current ? knob : 0;
    else {
      // PROXIMITY — the set level inside the inner half, easing to nothing at
      // the radius. A hard cutoff at the edge pops, and a pop reads as a bug
      // rather than as walking out of earshot.
      const inner = radius * 0.5;
      level = dist <= inner ? knob : dist >= radius ? 0 : knob * (1 - (dist - inner) / (radius - inner));
    }

    // Standing at it. CONSTANTS, not columns: the whole point is fewer knobs,
    // and "close enough to have walked up to it" is a property of bodies rather
    // than of a fixture. Both are capped by the trigger radius so a deliberately
    // tight 2u bubble does not get a slide that starts outside its own range.
    const slideTo = Math.min(SLIDE_U, radius);
    const full = fullRing;
    if (silentRef.current) level = 0;   // a scene room: silent until its play button is pressed
    if (level > 0 && dist < slideTo) {
      // 1 inside the tight bound (a plateau — you have arrived), easing back to
      // the room level at the outer edge of the slide.
      const t = dist <= full ? 1 : 1 - (dist - full) / (slideTo - full);
      level += (1 - level) * t;
    }
    // Only talk to the mixer when it actually changed — this runs every frame
    // for every screen, and setScreenLevel recomputes the loudest one.
    if (Math.abs(level - lvlRef.current) > 0.01 || (level === 0) !== (lvlRef.current === 0)) {
      lvlRef.current = level;
      setScreenLevel(podKind, p.placement_id, level);
    }

    // Keep the range rings flat on the walking plane and under the fixture,
    // whatever the fixture is rotated to. They live inside this group (so they
    // follow the surface around as it is nudged) but must not inherit its
    // rotation — a ring tilted onto a wall is not a footprint.
    const ring = ringRef.current;
    if (ring) {
      const target = TMP_V.set(wp.x, camera.position.y - EYE, wp.z);
      groupRef.current.worldToLocal(target);
      ring.position.copy(target);
      // Undo the parent's world rotation, then lie flat.
      ring.quaternion.copy(groupRef.current.getWorldQuaternion(TMP_Q).invert()).multiply(FLAT_Q);
    }
  });

  // Build/tear down the actual <video> only when the slot is granted/revoked.
  useEffect(() => {
    if (!playing || !animated) { setVidTex(null); return undefined; }
    const v = document.createElement('video');
    // THROUGH THE PROXY, ALWAYS — this is why the cards were black.
    //
    // A VideoTexture is CORS-gated exactly like an image texture, and the GCS
    // buckets send no Access-Control-Allow-Origin, so `crossOrigin='anonymous'`
    // + a signed storage.googleapis.com URL fails the check and the texture
    // never gets any frames. It fails silently as BLACK rather than as an error,
    // which is what made it look like a missing video instead of a blocked one.
    //
    // assets.proxied() re-routes gs:// and signed GCS URLs through
    // /api/assets/file, which re-signs by bucket+name and supports Range — so
    // seeking and iOS playback still work.
    v.crossOrigin = 'anonymous'; v.src = proxied(animated);
    v.loop = true; v.muted = true; v.defaultMuted = true; v.playsInline = true; v.preload = 'auto';
    v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
    Object.assign(v.style, { position: 'fixed', left: '-9999px', top: '0', width: '2px', height: '2px', opacity: '0', pointerEvents: 'none' });
    document.body.appendChild(v);
    v.play().catch(() => {});
    const t = new THREE.VideoTexture(v);
    t.colorSpace = THREE.SRGBColorSpace;
    setVidTex(t);
    return () => {
      try { v.pause(); v.removeAttribute('src'); v.load(); document.body.removeChild(v); } catch { /* gone */ }
      t.dispose();
      setVidTex(null);
    };
  }, [playing, animated]);

  useEffect(() => {
    if (!url || !/^https?:|^\//.test(url)) return undefined;
    let dead = false;
    // Shared learning loader: signed URL direct when the host allows it, proxy
    // otherwise — the route is remembered per host so only the first image pays
    // a failed attempt.
    loadTexture(url).then((t) => { if (!dead && t) setTex(t); });
    return () => { dead = true; };
  }, [url]);

  // ── SOUND ON A PLAYING SCREEN ─────────────────────────────────────────────
  //
  // Hover to listen, exactly like the news TVs already do: the narration comes
  // to the top of the mix and the station dips underneath, and rolling off hands
  // it back. Nothing plays on its own — autoplay would be both blocked and rude.
  //
  // MUTE (Khari, 2026-08-04: "it should include some sort of mute button too")
  // is global and sticky, not per-screen: there is one narration, so there is
  // one switch, and silencing a screen you walked away from should stay silent.
  // It also covers the case he hit — clicking the screen opens the artist's
  // music, and two things talking at once needs a way out.
  const [muted, setMuted] = useState(isPodcastMuted());
  const hoverRef = useRef(false);
  useEffect(() => onPodcastMute(setMuted), []);

  // The knob follows the row, and the room registry lets a scene say "turn that
  // up" by naming a ROOM rather than a placement id it cannot know.
  // A ROOM WITH ITS OWN SCENE starts the screen SILENT, with a play button
  // (Khari, 2026-10-23, in the salon: "it needs to default as muted, with a play
  // button on it… the TV is in a scene where the scene's narrative should be
  // heard instead"). Pressed, it is an ordinary screen: heard by proximity, with
  // its mute button. Every visit to the room starts silent again.
  const sceneRoom = !!(p.zone && ROOMS[p.zone]?.ownSound);
  const [turnedOn, setTurnedOn] = useState(false);
  const silentUntilPlayed = episodeMode && sceneRoom && !turnedOn;
  const silentRef = useRef(silentUntilPlayed);
  silentRef.current = silentUntilPlayed;
  const trigger = p.trigger_mode || 'hover';
  const baseVol = p.volume == null ? 1 : p.volume;
  const radius = p.trigger_radius || 8;
  // The full-volume bound, clamped so a tight radius cannot produce an inner
  // ring larger than the audible range. Derived once and used BOTH by the level
  // maths and by the ring you see on the ground — so what is drawn is what is
  // heard, rather than two constants that drift apart.
  const fullRing = Math.min(FULL_U, Math.min(SLIDE_U, radius) * 0.75);
  useEffect(() => {
    if (!episodeMode) return undefined;
    setBaseVolume(p.placement_id, baseVol);
    registerScreen(p.placement_id, p.zone || null, podKind);
    return () => { clearScreenLevel(podKind, p.placement_id); unregisterScreen(p.placement_id); };
  }, [episodeMode, p.placement_id, p.zone, podKind, baseVol]);

  // Hover is now just one of three triggers, and it only sets a flag — the level
  // is computed in one place below, so hover / proximity / always cannot
  // disagree about how loud this screen is.
  const listenOver = () => { if (episodeMode) hoverRef.current = true; };
  const listenOut = () => { hoverRef.current = false; };

  const w = p.width, h = p.height;
  const frame = FRAME_COLOR[p.frame_style] ?? '#0d0a08';
  // Screens glow after dark: base emissive lifted toward the night multiplier.
  const glow = (p.emissive?.base ?? 0.6) + night * ((p.emissive?.night ?? 2) - (p.emissive?.base ?? 0.6));

  return (
    <group
      ref={groupRef}
      position={p.pos}
      quaternion={p.quat}
      userData={{ isPlacement: true }}
      raycast={ghost ? () => null : undefined}
    >
      {/* Stand the whole fixture off the wall by its own depth. Placements sit
          only 0.02m proud, but the nameplate housing reaches 0.12m BACK — so it
          buried itself in the brickwork and the wall cut through it. Lifting the
          entire fixture (art + housing + plate together) keeps every internal
          z-relationship intact while clearing the wall. */}
      <group position={[0, 0, p.frame_style === 'nameplate' ? 0.14 : 0]}>
      {/* frame prop — a slightly larger backing plate behind the media */}
      {frame && (
        <mesh position={[0, 0, -0.03]} raycast={ghost ? noRaycast : undefined}>
          <planeGeometry args={[w * 1.06, h * 1.08]} />
          <meshStandardMaterial
            color={frame} roughness={0.6} metalness={0.3}
            emissive={p.frame_style === 'neon' ? '#ff5e78' : '#000000'}
            emissiveIntensity={p.frame_style === 'neon' ? 0.6 + night * 1.4 : 0}
            transparent={ghost} opacity={ghost ? 0.5 : 1}
          />
        </mesh>
      )}
      {/* the media surface — click it in edit mode to SELECT (not stack a new one) */}
      <mesh
        raycast={ghost ? noRaycast : undefined}
        onClick={(onSelect || onActivate) ? (e) => {
          e.stopPropagation();
          if (onSelect) onSelect(p.placement_id);          // admin: select to edit
          else onActivate(postsBoard && active ? null : p.placement_id);   // visitor: open the board (a posts board: toggle the caption)
        } : undefined}
        onPointerOver={(e) => { e.stopPropagation(); if (onSelect || onActivate) document.body.style.cursor = 'pointer'; listenOver(); }}
        onPointerOut={() => { if (onSelect || onActivate) document.body.style.cursor = 'auto'; listenOut(); }}
      >
        <planeGeometry args={[w, h]} />
        {/* ONE keyed material, not two branches in the same slot: React would
            reconcile those as an update, leaving the material compiled without
            USE_MAP when the texture lands late (and resetting color to white).
            Keying on the texture forces a fresh, correctly-compiled material. */}
        {(() => {
          const media = vidTex || filmTex || tex;
          // PAINT, NOT A SCREEN (Khari, 2026-08-06, placing a mural PNG: "doesn't
          // adjust to the environment light and seems not to honor the
          // transparent area").
          //
          // Everything above this line was authored for LIT FIXTURES — a
          // billboard, a jumbotron, a marquee. Those EMIT: the image goes in as
          // an emissiveMap with toneMapping off, so it stays bright regardless
          // of the sky and lifts further after dark. Exactly wrong for a mural,
          // which is pigment on brick: it should be LIT BY the world and go dark
          // with it, and at night it should fall into shadow rather than glow.
          //
          // And `transparent` was `media ? ghost : true` — false for any real
          // image — so a PNG's alpha channel was discarded outright and cut-out
          // areas filled in opaque. That is the white background.
          //
          // `poster_wall` already existed in the surface_type enum (migration
          // 251) and has always been the right name for this, so no migration:
          // the renderer simply never asked what KIND of surface it was drawing.
          const paint = p.surface_type === 'poster_wall';
          return (
            <meshStandardMaterial
              key={`${paint ? 'paint' : 'screen'}:${media ? media.uuid : 'blank'}`}
              map={media || null}
              // No emissive channel at all on paint — that is the whole difference.
              emissiveMap={paint ? null : (media || null)}
              color={media ? '#ffffff' : (ghost ? '#7dd3ff' : '#1b1622')}
              emissive={paint ? '#000000' : (media ? '#ffffff' : (ghost ? '#7dd3ff' : '#ffb25e'))}
              emissiveIntensity={paint ? 0 : (media ? glow : (ghost ? 0.8 : glow * 0.5))}
              // Paint lives inside the scene's tone mapping like every other
              // lit surface; a screen deliberately sits outside it.
              toneMapped={paint ? true : !media}
              // alphaTest keeps the cut-out crisp AND keeps depth writing, so a
              // mural with holes in it cannot sort badly against the wall it is
              // painted on. transparent alone would do neither reliably.
              transparent={paint ? true : (media ? ghost : true)}
              alphaTest={paint ? 0.06 : 0}
              roughness={paint ? 0.94 : undefined}
              metalness={paint ? 0 : undefined}
              opacity={media ? (ghost ? 0.55 : 1) : (ghost ? 0.42 : 1)}
              side={THREE.DoubleSide}
            />
          );
        })()}
      </mesh>

      {/* ── THE MUTE BUTTON ──────────────────────────────────────────────────
          Only on a screen that can actually make noise, and only outside the
          admin tool (where a click means "select this surface"). Bottom-left so
          it never lands under the nameplate's close × or the frame's top edge.

          A speaker glyph rather than a word: it reads at billboard distance and
          in any language, and it is the one control on the surface so there is
          nothing to disambiguate it from. */}
      {/* THE PLAY BUTTON — a scene room's screen, until it is pressed. */}
      {episodeMode && !ghost && !onSelect && silentUntilPlayed && (() => {
        const r = Math.min(w, h) * 0.16;
        return (
          <group position={[0, 0, 0.03]}>
            <mesh
              onClick={(e) => { e.stopPropagation(); setTurnedOn(true); if (muted) setPodcastMuted(false); }}
              onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
              onPointerOut={() => { document.body.style.cursor = 'auto'; }}
            >
              <circleGeometry args={[r, 32]} />
              <meshBasicMaterial color="#08060a" transparent opacity={0.62} toneMapped={false} />
            </mesh>
            {/* The triangle as geometry: the in-world text font has no ▶ glyph. */}
            <mesh position={[r * 0.1, 0, 0.01]} raycast={() => null}>
              <circleGeometry args={[r * 0.5, 3]} />
              <meshBasicMaterial color="#ffd27d" toneMapped={false} />
            </mesh>
            <Text position={[0, -r * 1.35, 0.01]} fontSize={r * 0.32} color="#ffe9c4" anchorX="center" anchorY="middle" toneMapped={false}>Play with sound</Text>
          </group>
        );
      })()}
      {episodeMode && !ghost && !onSelect && !silentUntilPlayed && (() => {
        const r = Math.min(w, h) * 0.075;
        const bx = -w / 2 + r * 1.6, by = -h / 2 + r * 1.6;
        return (
          <group position={[bx, by, 0.02]}>
            <mesh
              onClick={(e) => { e.stopPropagation(); setPodcastMuted(!muted); }}
              onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; listenOver(); }}
              onPointerOut={() => { document.body.style.cursor = 'auto'; listenOut(); }}
            >
              <circleGeometry args={[r, 24]} />
              <meshBasicMaterial color="#08060a" transparent opacity={0.66} toneMapped={false} />
            </mesh>
            <Text
              position={[0, 0, 0.01]} fontSize={r * 1.05}
              color={muted ? '#ff8a9c' : '#ffd27d'}
              anchorX="center" anchorY="middle" toneMapped={false}
            >
              {muted ? '🔇' : '🔊'}
            </Text>
          </group>
        );
      })()}

      {/* NAMEPLATE fixture: the artwork keeps its exact aspect and a recessed
          plate hangs BELOW it with the name as live text. Built from geometry so
          it fits any size/aspect — no model to author or load. */}
      {p.frame_style === 'nameplate' && (() => {
        const margin = h * 0.035;
        const plateH = h * 0.17;
        const backW = w + margin * 2;
        const backH = h + plateH + margin * 2;
        const backY = -plateH / 2;                       // grows downward
        const plateY = -h / 2 - plateH / 2;
        const name = (media?.name || p.label || '').slice(0, 34);
        const sub = (media?.sub || '').slice(0, 40);
        return (
          <group>
            {/* Housing — a real box so the sign has thickness, not a sticker.
                Depth 0.1 centred at -0.07 puts its FRONT FACE at -0.02, a clear
                2cm behind the media plane (z=0). Centring at -0.05 made the front
                face exactly coplanar with the artwork and they z-fought, which
                shredded the images into black blocks. The extra depth just sinks
                into the wall, where it's invisible. */}
            <mesh position={[0, backY, -0.07]} castShadow>
              <boxGeometry args={[backW, backH, 0.1]} />
              <meshStandardMaterial color="#100c0a" roughness={0.55} metalness={0.35} />
            </mesh>
            {/* recessed dark field the text sits in */}
            <mesh position={[0, plateY, 0.012]}>
              <planeGeometry args={[w * 0.985, plateH * 0.82]} />
              <meshStandardMaterial
                color="#050406" roughness={0.9}
                emissive="#05040a" emissiveIntensity={0.25 + night * 0.5}
              />
            </mesh>
            {/* warm hairline separating art from plate — the bit that reads as craft */}
            <mesh position={[0, -h / 2 - plateH * 0.045, 0.014]}>
              <planeGeometry args={[w * 0.985, Math.max(0.008, h * 0.005)]} />
              <meshBasicMaterial color="#ffb25e" transparent opacity={0.55 + night * 0.4} toneMapped={false} />
            </mesh>
            {/* the NAME — SDF text stays crisp at any distance; toneMapped off so
                it holds up when the board glows at night */}
            {name && (
              <Text
                position={[0, plateY + (sub ? plateH * 0.13 : 0), 0.02]}
                // AUTO-FIT: shrink long names so they stay on ONE centred line
                // instead of wrapping out of the plate ("MALAKI THE MENACE" was
                // spilling onto a second line and colliding with the sub-line).
                // ~0.62em average glyph advance for uppercase + letterSpacing.
                fontSize={Math.min(
                  plateH * (sub ? 0.4 : 0.46),
                  (w * 0.9) / Math.max(6, name.length * 0.62),
                )}
                maxWidth={w * 0.94}
                whiteSpace="nowrap"
                color="#fff3dc" anchorX="center" anchorY="middle"
                letterSpacing={0.04} toneMapped={false}
                outlineWidth={plateH * 0.012} outlineColor="#000"
              >
                {name.toUpperCase()}
              </Text>
            )}
            {sub && (
              <Text
                position={[0, plateY - plateH * 0.22, 0.02]}
                fontSize={plateH * 0.2} maxWidth={w * 0.86}
                color="#ffb25e" anchorX="center" anchorY="middle"
                letterSpacing={0.12} toneMapped={false}
              >
                {sub.toUpperCase()}
              </Text>
            )}
          </group>
        );
      })()}

      {/* OPENED, A POSTS BOARD: the post's caption over its picture */}
      {active && postsBoard && media?.caption != null && (
        <PostCaption item={media} w={w} h={h} onClose={() => onActivate?.(null)} />
      )}
      {/* OPENED: the artist's tracks as a clickable artwork grid */}
      {active && !(postsBoard && media?.caption != null) && media?.avatarId && (
        <BillboardTracks
          avatarId={media.avatarId}
          name={media.name}
          w={w} h={h}
          onClose={() => onActivate?.(null)}
        />
      )}

      {/* house placeholder text so an unbooked surface still reads as inventory */}
      {!tex && !vidTex && (
        <Text position={[0, 0, 0.02]} fontSize={Math.min(w, h) * 0.12} color="#ffe9c4" anchorX="center" anchorY="middle" maxWidth={w * 0.9}>
          {ghost ? `${p.aspect} · ${p.width.toFixed(1)}m` : (p.label || p.surface_type)}
        </Text>
      )}
      {/* selection / edit wireframe */}
      {(selected || ghost) && (
        <lineSegments position={[0, 0, 0.03]}>
          <edgesGeometry args={[new THREE.PlaneGeometry(w, h)]} />
          <lineBasicMaterial color={ghost ? '#7dd3ff' : '#9dff7d'} />
        </lineSegments>
      )}
      </group>

      {/* ── AUDIO RANGE ON THE GROUND ────────────────────────────────────────
          Two rings, because the sound has two edges and tuning one without
          seeing the other is guesswork:
            outer  `trigger_radius` — where it stops being audible at all
            inner  FULL_U — inside here it plays at 100%, so this is the ring
                   you walk into to hear it over everything else
          Admin-only, and only for a screen that can actually make noise.

          Drawn on the plane the PLAYER walks on (camera Y minus eye height)
          rather than at the fixture's own base: a TV mounted at head height
          would otherwise draw its range floating in mid-air, and the whole
          point is to see the footprint you walk through. That also makes it
          correct on sloped ground and indoors without knowing either. */}
      {!!onSelect && episodeMode && (
        <group ref={ringRef}>
          <mesh>
            <ringGeometry args={[radius - 0.06, radius, 72]} />
            <meshBasicMaterial color="#7dd3ff" transparent opacity={selected ? 0.85 : 0.35} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
          <mesh position={[0, 0, 0.002]}>
            <ringGeometry args={[Math.max(0.05, fullRing - 0.05), fullRing, 56]} />
            <meshBasicMaterial color="#9dff7d" transparent opacity={selected ? 0.9 : 0.4} toneMapped={false} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        </group>
      )}
    </group>
  );
}

export default function PlacementLayer() {
  const { camera, scene } = useThree();
  const placements = useWorldStore((s) => s.placements);
  const setPlacements = useWorldStore((s) => s.setPlacements);
  const draft = useWorldStore((s) => s.placementDraft);
  const selected = useWorldStore((s) => s.selectedPlacement);
  const setSelected = useWorldStore((s) => s.setSelectedPlacement);
  const activeBillboard = useWorldStore((s) => s.activeBillboard);
  const setActiveBillboard = useWorldStore((s) => s.setActiveBillboard);

  const editing = useWorldStore((s) => s.adminTool) === 'placements';
  const tod = useWorldTime(SLICE_DISTRICT.timezone);
  const night = tod?.night ?? 0;

  // ── WHAT THIS LAYER OWNS: THE WORLD ────────────────────────────────────────
  //
  // `zone` is the room a placement belongs to, or null for the street. It has
  // been in the table since 251 and nothing ever wrote it, so using it costs no
  // migration.
  //
  // A surface with a zone is NOT rendered here at all — RoomPlacements draws it
  // inside the room's group, where its stored coordinates are meaningful. This
  // layer draws only world-space surfaces, and then only when the world is
  // actually being drawn:
  //
  //   street        yes
  //   SITED room    yes — the market stands at its real building with the city
  //                 rendering through its windows, so the street's surfaces are
  //                 genuinely in view
  //   virtual room  no — instanced at world origin with the city hidden, so a
  //                 street surface would be a billboard in the middle of the
  //                 shop floor. That is what used to happen, and why this layer
  //                 was gated to the street rather than scoped.
  const interior = useWorldStore((s) => s.interior);
  const sited = interior && ROOMS[interior]?.placement === 'sited';
  const inScope = (p) => !p.zone && (!interior || sited);

  // Load the inventory (public read). While the placement tool is open it also
  // pulls HIDDEN surfaces, so something switched off is still selectable and can
  // be switched back on — otherwise Hide would be Delete with a longer name.
  // Hidden ones are skipped by the renderer below; they exist here to be found.
  useEffect(() => {
    let dead = false;
    api.placements(editing).then((d) => {
      if (dead) return;
      const all = d?.placements || [];
      setPlacements(all);
      // Where each one will render, stated once on load. "It showed up in the
      // wrong place" is otherwise a question you can only answer by walking to
      // both places and looking.
      const by = {};
      for (const p of all) { const k = p.zone || 'street'; by[k] = (by[k] || 0) + 1; }
      console.log(`[placement] ${all.length} loaded — ${Object.entries(by).map(([k, n]) => `${n} ${k}`).join(', ') || 'none'}`);
    }).catch(() => {});
    return () => { dead = true; };
  }, [setPlacements, editing]);

  // ── Ghost: raycast from the crosshair onto world geometry, orient to normal ──
  const [ghost, setGhost] = useState(null);
  const ray = useMemo(() => new THREE.Raycaster(), []);
  const ghostRef = useRef(null);
  // Publish the live ghost transform so the panel's Save can read it.
  useEffect(() => { window.__placementGhost = ghostRef; }, []);

  useFrame(() => {
    if (!editing) { if (ghost) setGhost(null); return; }
    ray.setFromCamera({ x: 0, y: 0 }, camera);           // dead centre = crosshair
    const hits = ray.intersectObjects(scene.children, true).filter((h) => {
      // never snap to our own surfaces/ghost, helpers, or sprites
      let o = h.object;
      while (o) { if (o.userData?.isPlacement) return false; o = o.parent; }
      return h.face && h.distance > 0.5 && h.distance < 120;
    });
    const hit = hits[0];
    if (!hit) return;
    // World-space normal of the hit face, pushed 2cm off the wall (no z-fighting).
    const n = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
    const pos = hit.point.clone().addScaledVector(n, 0.02);
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    const next = {
      pos: [pos.x, pos.y, pos.z],
      quat: [quat.x, quat.y, quat.z, quat.w],
      width: draft.width,
      height: draft.width / (ASPECT_RATIO[draft.aspect] || 1),
      aspect: draft.aspect,
      frame_style: draft.frame_style,
      surface_type: draft.surface_type,
      label: draft.label,
      emissive: { base: 0.6, night: 2 },
    };
    ghostRef.current = next;
    // Only re-render when it moved meaningfully (keeps this cheap per frame).
    setGhost((g) => {
      if (g && Math.abs(g.pos[0] - next.pos[0]) < 0.02 && Math.abs(g.pos[1] - next.pos[1]) < 0.02
        && Math.abs(g.pos[2] - next.pos[2]) < 0.02 && g.width === next.width && g.aspect === next.aspect
        && g.frame_style === next.frame_style) return g;
      return next;
    });
  });

  return (
    <group>
      {editing && <CameraBasisProbe />}
      <PlacementSet list={placements.filter(inScope)} night={night} />
      {/* Ghost only while PLACING (nothing selected) — with a surface selected
          you're editing it, so no preview stacks on top of what you're editing.
          The transform ref stays live either way, so "Move here" still works.
          Stays at WORLD level in both cases: it is raycast against the scene, so
          it is a world-space object by construction. PlacementPanel converts it
          into room space on save. */}
      {editing && !selected && ghost && <Surface p={ghost} night={night} ghost />}
    </group>
  );
}

// ── ONE RENDERER, TWO SPACES ────────────────────────────────────────────────
// Where a <PlacementSet> is mounted decides what space its contents are in, and
// nothing else does. PlacementLayer mounts one at the scene root for the street;
// MeshInterior mounts another inside the room's anchor group. Same component,
// same behaviour, and a room-scoped placement inherits the room's transform for
// free rather than having it re-applied by a copy of the room's numbers.
function PlacementSet({ list, night }) {
  const draft = useWorldStore((s) => s.placementDraft);
  const selected = useWorldStore((s) => s.selectedPlacement);
  const setSelected = useWorldStore((s) => s.setSelectedPlacement);
  const activeBillboard = useWorldStore((s) => s.activeBillboard);
  const setActiveBillboard = useWorldStore((s) => s.setActiveBillboard);
  const editing = useWorldStore((s) => s.adminTool) === 'placements';

  return (
    <>
      {list.map((p) => {
        // A HIDDEN SURFACE DOES NOT RENDER — the label below still does, so it
        // stays findable and can be switched back on. Only reachable while the
        // tool is open, since that is the only time hidden rows are fetched.
        if (p.active === false) return null;
        const isSel = editing && selected === p.placement_id;
        // WATER is a placement with no content. It reuses this table because a
        // placement already describes everything a pool needs — a position, an
        // orientation and a footprint, authored with a tool that raycasts the
        // real ground — so water gets placed and resized exactly like any other
        // surface, instead of as coordinates baked into a component.
        //
        // Rendered before the ad-surface branch so none of the frame, emissive
        // or content machinery is built for something that shows a shader.
        if (p.surface_type === 'water' || (isSel && draft.surface_type === 'water')) {
          const w = isSel ? draft.width : p.width;
          const h = isSel ? draft.width / (ASPECT_RATIO[draft.aspect] || 1) : p.height;
          return (
            <SeaVista
              key={p.placement_id}
              position={(isSel && draft.pos) || p.pos}
              quaternion={(isSel && draft.quat) || p.quat}
              size={[w, h]}
            />
          );
        }
        // LIVE PREVIEW: while a surface is selected, render it with the panel's
        // (unsaved) draft applied, so size/aspect/frame/content changes are visible
        // instantly. Apply then persists exactly what you're looking at.
        const shown = isSel ? {
          ...p,
          width: draft.width,
          height: draft.width / (ASPECT_RATIO[draft.aspect] || 1),
          aspect: draft.aspect,
          frame_style: draft.frame_style,
          surface_type: draft.surface_type,
          content_policy: draft.content_policy,
          // Sound previews too, or the range rings on the ground would keep
          // drawing the SAVED radius while you drag the slider — a tuning aid
          // that does not move while you tune is worse than none.
          playback: draft.playback || p.playback,
          trigger_mode: draft.trigger_mode || p.trigger_mode,
          volume: draft.volume ?? p.volume,
          trigger_radius: draft.trigger_radius ?? p.trigger_radius,
          default_content_ref: draft.default_content_ref || null,
          label: draft.label || p.label,
          // Nudged transform previews too — moving a surface has to be visible
          // while you move it, not after you save.
          pos: draft.pos || p.pos,
          quat: draft.quat || p.quat,
        } : p;
        // PORTAL PLACEMENT (POC): a surface whose content ref reads
        // `portal:<destination>` isn't an ad slot — it's a doorway. Same saved
        // geometry, different behaviour, so the existing tool places it. While
        // EDITING it still renders as a normal Surface, or you couldn't see and
        // resize what you're shaping.
        if (!editing && isPortal(shown)) {
          return <PortalSurface key={p.placement_id} placement={shown} night={night} />;
        }
        return (
          <Surface
            key={p.placement_id}
            p={shown}
            night={night}
            selected={isSel}
            onSelect={editing ? setSelected : null}
            onActivate={editing ? null : setActiveBillboard}
            active={!editing && activeBillboard === p.placement_id}
          />
        );
      })}
      {/* debug overlay: label every surface while placing, so density is visible.
          Inside the set, so a room's labels are positioned in the room's space
          exactly as the surfaces they name. */}
      {editing && list.map((p) => (
        <Text
          key={`lbl-${p.placement_id}`}
          position={[p.pos[0], p.pos[1] + p.height / 2 + 0.35, p.pos[2]]}
          fontSize={0.3} color={p.active === false ? '#7dd3ff' : '#9dff7d'} anchorX="center" anchorY="middle"
          outlineWidth={0.01} outlineColor="#000"
        >
          {`#${p.placement_id} ${p.label || p.surface_type}${p.active === false ? ' (hidden)' : ''}`}
        </Text>
      ))}
    </>
  );
}

// The room's own surfaces, mounted by MeshInterior INSIDE the room's anchor
// group — which is what makes their stored coordinates room-relative.
export function RoomPlacements({ roomId }) {
  const placements = useWorldStore((s) => s.placements);
  const tod = useWorldTime(SLICE_DISTRICT.timezone);
  const mine = placements.filter((p) => (p.zone || null) === roomId);
  if (!mine.length) return null;
  return <PlacementSet list={mine} night={tod?.night ?? 0} />;
}
